import React, { useState, useEffect, useRef, useCallback } from 'react';
import './../../styles/Main.css';
import './../../styles/WindowsPage.css';
import DownloadButton from '../Buttons/DownloadButton';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { useNavigate, useParams } from 'react-router-dom';
import getBaseUrl from '../../utils/baseUrl';
import { useAuth } from '../../context/AuthContext';
import VideoModal from './../modals/VideoModal';
import useFetchDocuments from './../hooks/useFetchDocuments';

function WindowsPage() {
  const [documents, setDocuments] = useState([]);
  const [content, setContent] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [videoUrl, setVideoUrl] = useState(null); // State for video URL
  const [isModalOpen, setIsModalOpen] = useState(false); // State for modal visibility
  const contentRef = useRef(null);
  const { msiFile, msiUrl } = useAuth();
  const navigate = useNavigate();
  const { documentName } = useParams();

  const fetchContent = useCallback((fileName) => {
    fetch(`${getBaseUrl()}/guides/windows/${fileName}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.text();
      })
      .then((text) => {
        let htmlContent = marked(text, { gfm: true, breaks: true });
        const baseUrl = getBaseUrl();
        htmlContent = htmlContent.replace(/src="(?:\.\/)?images\//gi, `src="${baseUrl}/guides/windows/Images/`);

        const sanitizedContent = DOMPurify.sanitize(htmlContent);
        setContent(sanitizedContent);

        setTimeout(() => addLinkClickListener(), 100);
      })
      .catch((error) => {
        console.error('Error fetching the HTML file');
        setContent('Error loading content. Please try again later.');
      });
  }, []);

  useFetchDocuments('Windows', documentName, setDocuments, fetchContent);
  
  useEffect(() => {
    if (contentRef.current) {
      addLinkClickListener();
    }
  }, [content]);

  const addLinkClickListener = () => {
    if (contentRef.current) {
      const links = contentRef.current.querySelectorAll('a');
      links.forEach(link => {
        link.removeEventListener('click', handleLinkClick); // Remove previous listeners
        link.addEventListener('click', handleLinkClick);
      });
    }
  };

  const handleLinkClick = (event) => {
    event.preventDefault();
    const href = event.currentTarget.getAttribute('href');
    if (href && (href.endsWith('.mp4') || href.endsWith('.mov') || href.endsWith('.webm'))) {     
      const videoPath = href.startsWith('/') ? href : `${getBaseUrl()}/${href}`;
      setVideoUrl(videoPath);
      setIsModalOpen(true);
    } else if (href && href.startsWith('guides/windows/')) {
      const documentName = href.split('/').pop();
      navigate(`/windows/${documentName}`);
    } else {
      window.location.href = href;
    }
  };  

  // Helper function to escape special regex characters
  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value.length > 2) {
      const escapedValue = escapeRegExp(value);
      const regex = new RegExp(`(${escapedValue})`, 'gi');
      const highlightedContent = content.replace(regex, '<span class="highlight">$1</span>');
      setContent(DOMPurify.sanitize(highlightedContent));

      setTimeout(() => {
        const firstHighlight = document.querySelector('.highlight');
        if (firstHighlight) {
          firstHighlight.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } else {
      fetchContent(documentName);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setVideoUrl(null);
  };

  return (
    <div className="windows-page-layout">
      <div className="windows-header">
        <p className="windows-title"></p>
        <div className="search-input-button-container">
          <div className="search-bar">
            <input
              type="text"
              className="search-input"
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <button className="search-button" title="Clear" onClick={() => setSearchTerm('')}>Clear</button>
        </div>
      </div>
      <div className="windows-content">
        <div className="osversion-details">
          {content && (
            <div className="windows-markdown-content">
              <div className="windows-title-box">
                <h1 className="windows-title-text">Install eSafe for Windows</h1>
              </div>
              <div ref={contentRef} dangerouslySetInnerHTML={{ __html: content }} />
              <div className='windows-download-section'>
                <DownloadButton link={msiUrl} title={`Download Windows Installer`} />
              </div>
            </div>
          )}
        </div>
      </div>
      {isModalOpen && (
        <VideoModal videoUrl={videoUrl} isOpen={isModalOpen} onClose={closeModal} />
      )}
    </div>
  );
}

export default WindowsPage;
